import {
  reconcileAdConsent,
  type AccountAdConsent,
  type AdConsentSource,
} from '@/constants/adConsent';
import { adConsentStore, isGpcEnabled, markAdConsentAccountSettled } from '@/utils/adConsentStore';
import { apiClient } from '@/utils/apiClient';

type Upload = { granted: boolean; source: AdConsentSource };

/**
 * Settle this browser against the account's advertising-partner choice and
 * return what, if anything, the account should be told, by the clock-free rules
 * in `reconcileAdConsent` (shared with mobile). Global Privacy Control is an
 * opt-out for the known voter as well as the browser, so under it the device
 * records a denial and the account is switched off unless it already is; an
 * undecided account counts as on.
 */
export function settleAdConsent(account: AccountAdConsent | null | undefined): Upload | undefined {
  if (isGpcEnabled()) {
    if (adConsentStore.get()?.granted !== false) adConsentStore.set(false, 'gpc');
    return account?.granted === false ? undefined : { granted: false, source: 'gpc' };
  }
  const { applyToDevice, uploadToAccount } = reconcileAdConsent(adConsentStore.get(), account);
  if (applyToDevice) adConsentStore.apply(applyToDevice);
  return uploadToAccount;
}

/** Sign-in reconciliation; the upload is best-effort and retried at the next sign-in. */
export function applyAccountAdConsent({
  email,
  accessToken,
  account,
}: {
  email: string | undefined;
  accessToken: string;
  account: AccountAdConsent | null | undefined;
}): void {
  const upload = settleAdConsent(account);
  markAdConsentAccountSettled();
  const uploading = adConsentStore.get();
  if (!upload || !email || !uploading) return;
  void apiClient('/user/settings', {
    method: 'POST',
    accessToken,
    body: JSON.stringify({ email, advertising_consent: upload }),
  })
    .then(() => adConsentStore.markSynced(uploading))
    .catch(() => undefined);
}
